import { sampleGroupZod } from 'packages/lib/api/annotations/contract/sample-group';
import { z } from 'zod';

export const RequestStatusSchema = z.enum([
  'pending_approval',
  'approved',
  'rejected',
  'in_progress',
  'complete',
  'hold',
  'problem',
]);

export type RequestStatus = z.infer<typeof RequestStatusSchema>;

export const PrioritySchema = z.enum(['P00', 'P0', 'P1', 'P2', 'P3']);

export type Priority = z.infer<typeof PrioritySchema>;

export const TeamSchema = z.enum([
  'Detection',
  'Semantics',
  'ML Planner',
  'Prediction',
  'Tracking',
  'GT Regression',
  'AIT',
]);

export type Team = z.infer<typeof TeamSchema>;

export const RequestTypeSchema = z.enum([
  'Full Scene LidarBox',
  'Autolabeled Full Scene Lidarbox',
  'Partial Scene LidarBox',
  'Combined LidarTopDown',
  'Full Scene LidarTopDown',
  'Partial Scene LidarTopDown',
  'Construction LidarTopDown',
  'Traffic Light Detection',
  'Video Labeling',
  'Image Enrichment',
  'Scene Enrichment',
  'Lidarbox Amplification (add new annotations)',
  'Lidarbox Enrichment (reclassification only, classes/attributes)',
  'InstanceSeg',
  'Partial InstanceSeg',
  'SemSeg',
  'Collection only (logs, no annotation)',
  'Collection only (logs and timestamps, no annotation)',
  'Pylon Reclassification (no annotation)',
  'New Workflow Required',
]);

export type RequestType = z.infer<typeof RequestTypeSchema>;

export const RequestSourceEnumSchema = z.enum([
  'Autonomy Team to Provide',
  'New Collection - Mapped, Manual',
  'New Collection - Mapless, Manual',
  'Existing Logs (no new collection required)',
  'Other/Unsure',
]);

export type RequestSourceEnum = z.infer<typeof RequestSourceEnumSchema>;

export const DataSourceSchema = z.object({
  request_data_source: z.string(),
  request_data_source_search_method: z.string().nullable(),
  request_data_source_due_date: z.coerce.date().optional().nullable(),
});

export type DataSource = z.infer<typeof DataSourceSchema>;

export const CommentSchema = z.object({
  comment_id: z.string().uuid(),
  request_id: z.string().uuid(),
  user_id: z.string().uuid().nullable(),
  user_email: z.string().email(),
  comment_text: z.string(),
  created_at: z.coerce.date(),
  user: z
    .object({
      first_name: z.string(),
      last_name: z.string(),
      email: z.string().email(),
    })
    .nullable(),
});

export const DataRequestSchema = z.object({
  id: z.string().uuid(), // Internal UUID
  request_id: z.string().nullable(), // Human-readable ID, e.g., REQ01.Q1.2024
  requestor_id: z.string().uuid().nullable(),
  requestor_email: z.string().email().nullable(),
  requestor: z
    .object({
      first_name: z.string(),
      last_name: z.string(),
      email: z.string().email(),
    })
    .optional()
    .nullable(),
  name: z.string(),
  description: z.string().nullable(),
  status: RequestStatusSchema,
  priority: PrioritySchema,
  team: z.string().nullable(),
  request_type: z.string(),
  quantity: z.number().int().nullable(),
  quantity_text: z.string().nullable(),
  unit: z.string(),
  annotation_scene_length: z.string().nullable(),
  annotation_frequency: z.string().nullable(),
  workflow_token: z.string().nullable(),
  instruction_token: z.string().optional().nullable(),
  taxonomy_needs_update: z.boolean().default(false),
  infrastructure_needs_update: z.boolean().default(false),
  updates_text: z.string().nullable(),
  due_date: z.string().nullable(),
  due_date_comment: z.string().nullable(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  data_sources: z.array(DataSourceSchema).default([]),
  comments: z.array(CommentSchema).default([]),
  completionPercentage: z.number().min(0).max(100).default(0),
});

const DataRequestCreateSchema = DataRequestSchema.partial()
  .omit({
    id: true,
    request_id: true,
    created_at: true,
    updated_at: true,
  })
  .required({
    name: true,
    status: true,
    priority: true,
    team: true,
    request_type: true,
    quantity_text: true,
    unit: true,
    due_date: true,
    requestor_email: true,
  });

const DataRequestCommentCreateSchema = CommentSchema.partial()
  .omit({
    comment_id: true,
    created_at: true,
    user: true,
  })
  .required({
    comment_text: true,
    user_email: true,
    request_id: true,
  });

export const SampleGroupCreateSchema = sampleGroupZod
  .partial()
  .omit({
    token: true,
    createdAt: true,
    updatedAt: true,
  })
  .required({
    name: true,
    batchType: true,
    pipeline: true,
    requestId: true,
    owner: true,
    workflowToken: true,
    pipelineToken: true,
  });

export type DataRequest = z.infer<typeof DataRequestSchema>;
export type DataRequestSource = z.infer<typeof DataSourceSchema>;
export type DataRequestComment = z.infer<typeof CommentSchema>;
export type DataRequestCreate = z.infer<typeof DataRequestCreateSchema>;
export type DataRequestCommentCreate = z.infer<
  typeof DataRequestCommentCreateSchema
>;
export const DataRequestResponseSchema = z.array(DataRequestSchema);
export type DataRequestResponse = z.infer<typeof DataRequestResponseSchema>;

export type SampleGroupCreate = z.infer<typeof SampleGroupCreateSchema>;
